import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'

import BookedItemExtendDatesModal from './BookedItemExtendDatesModal'
import ConfirmNavigateToOfferModal from './ConfirmNavigateToOfferModal'
import ConfirmUpdateTripDatesModal from './ConfirmUpdateTripDatesModal'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineAngleRightBIcon from 'components/Luxkit/Icons/line/LineAngleRightBIcon'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import ListItem from 'components/Luxkit/List/ListItem'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import TripContext, { useTripId } from 'tripPlanner/contexts/TripContext'
import { useUpdateTrip } from 'tripPlanner/hooks/api'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { ItemMetadata } from 'tripPlanner/models'
import { TripItem } from 'tripPlanner/types/tripItem'
import { formatCasualDateRange, isLeBooking, isLeItem } from 'tripPlanner/utils'

const StyledModalContent = styled(ModalContent)`
  padding-bottom: ${rem(20)};
`

// This modal would not open if the trip did not have dates.
type TripContextWithDates = {
  startDate: moment.Moment
  endDate: moment.Moment
}

type TripItemWithDates = TripItem & {
  startDate: moment.Moment
  endDate?: moment.Moment
}

function generateMessage(
  item: TripItemWithDates,
  tripContext: TripContextWithDates,
  isItemMultiDate: boolean,
  isTripMultiDate: boolean,
) {
  const itemName = ItemMetadata[item.type].singularName.toLowerCase()
  const itemStartDate =
    'endDate' in item && item.endDate ?
      formatCasualDateRange(item.startDate, item.endDate) :
      item.startDate.format(DMY_CASUAL_SHORT_FORMAT)
  const tripDateRange = formatCasualDateRange(
    tripContext.startDate,
    tripContext.endDate,
  )

  let message = 'The date'
  if (isItemMultiDate) {
    message += 's'
  }

  message += ` you've chosen for this ${itemName} (${itemStartDate}) don't match your overall trip date`
  if (isTripMultiDate) {
    message += 's'
  }

  message += ` (${tripDateRange}).`

  return message
}

interface Props {
  item: TripItemWithDates
  changeItemDates: () => void
}

function UpdateItemOrTripDatesModal({ item, changeItemDates }: Props) {
  const tripContext = useContext(TripContext) as TripContextWithDates
  const tripId = useTripId()

  const openModal = useOpenModal()
  const { closeModal } = useModalCallbacks()

  const { mutate, isLoading } = useUpdateTrip({
    onSuccess: () => {
      closeModal()
      showSnackbar('Dates saved', 'success')
    },
    onError: () => {
      showSnackbar(
        'Error while updating trip dates. Please try again.',
        'critical',
      )
    },
  })

  const finalDates = useMemo(() => {
    let finalStartDate = tripContext.startDate
    let finalEndDate = tripContext.endDate
    const itemStartDate = item.startDate

    if (finalStartDate > itemStartDate) {
      finalStartDate = itemStartDate
    }

    if ('endDate' in item && item.endDate && finalEndDate < item.endDate) {
      finalEndDate = item.endDate
    } else if (itemStartDate > finalEndDate) {
      finalEndDate = itemStartDate
    }

    return { finalStartDate, finalEndDate }
  }, [item, tripContext.endDate, tripContext.startDate])

  const updateTripDates = useCallback(() => {
    const { finalStartDate, finalEndDate } = finalDates

    mutate({
      tripId,
      startDate: finalStartDate,
      endDate: finalEndDate,
    })
  }, [finalDates, mutate, tripId])

  const isMultiDateItem = !item.startDate.isSame(item.endDate, 'day')
  const isMultiDateTrip = !tripContext.startDate.isSame(
    tripContext.endDate,
    'day',
  )

  const Icon = ItemMetadata[item.type].icon

  const updateItem = () => {
    if (isLeItem(item)) {
      openModal(ConfirmNavigateToOfferModal, { changeItemDates, item })
    } else {
      changeItemDates()
    }
  }

  const confirmUpdateTripDates = () => {
    openModal(ConfirmUpdateTripDatesModal, {
      updateTripDates,
      currentTripDates: formatCasualDateRange(
        tripContext.startDate,
        tripContext.endDate,
      ),
      extendedTripDates: formatCasualDateRange(
        finalDates.finalStartDate,
        finalDates.finalEndDate,
      ),
      isLoading,
    })
  }

  if (isLeBooking(item)) {
    return (
      <BookedItemExtendDatesModal
        item={item}
        updateTripDates={updateTripDates}
        currentTripDates={formatCasualDateRange(
          tripContext.startDate,
          tripContext.endDate,
        )}
        extendedTripDates={formatCasualDateRange(
          finalDates.finalStartDate,
          finalDates.finalEndDate,
        )}
        isLoading={isLoading}
      />
    )
  }

  let modalHeaderTitle
  if (isMultiDateItem) {
    modalHeaderTitle = `${
      ItemMetadata[item.type].singularName
    } dates don't align with your trip`
  } else {
    modalHeaderTitle = `${
      ItemMetadata[item.type].singularName
    } date doesn't align with your trip`
  }

  const message = generateMessage(
    item,
    tripContext,
    isMultiDateItem,
    isMultiDateTrip,
  )

  return (
    <>
      <ModalHeader title={modalHeaderTitle} onCloseButtonClick={closeModal} />
      <ModalBody>
        <StyledModalContent>
          <VerticalSpacer gap={24}>
            <BodyText variant="large" colour="neutral-two">
              {message}
              <br />
              <br />
              <b>What would you like to do?</b>
            </BodyText>
          </VerticalSpacer>
          <div>
            {!isLeBooking(item) && (
              <ListItem
                startIcon={<Icon />}
                title={`Adjust ${ItemMetadata[
                  item.type
                ].singularName.toLowerCase()} dates`}
                endIcon={<LineAngleRightBIcon />}
                onClick={updateItem}
              />
            )}

            <ListItem
              startIcon={<LineCalendarIcon />}
              title="Extend trip duration"
              endIcon={<LineAngleRightBIcon />}
              onClick={confirmUpdateTripDates}
            />
          </div>
        </StyledModalContent>
      </ModalBody>
    </>
  )
}

export default UpdateItemOrTripDatesModal
